/**
 * <pdp-sticky-atc>: sticky add-to-cart bar for the product page.
 * See snippets/pdp-sticky-atc.liquid for why it lives outside <product-info>.
 */
if (!customElements.get('pdp-sticky-atc')) {
  customElements.define(
    'pdp-sticky-atc',
    class PdpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.select = this.querySelector('[data-sticky-variant]');
        this.idInput = this.querySelector('input[name="id"]');
        this.qtyInput = this.querySelector('.quantity__input');
        this.button = this.querySelector('button[type="submit"]');
        this.label = this.button?.querySelector('.sticky-atc__label');
        this.soldOutText = this.button?.querySelector('.sold-out-message');
        this.productForm = this.querySelector('product-form');
        this.visible = false;

        this.select?.addEventListener('change', () => {
          const option = this.select.selectedOptions[0];
          this.apply(option);
          this.selectOnPage(option);
        });

        // product-info.js sets the main form's variant id and fires "change" on every variant switch.
        this.onPageVariantChange = (event) => {
          if (event.target.matches?.(`#product-form-${this.sectionId} input[name="id"]`)) this.follow(event.target.value);
        };
        document.addEventListener('change', this.onPageVariantChange);

        this.observe();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onPageVariantChange);
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        // On a product swap Dawn removes the old page 500ms after the new one is live:
        // don't undo what the new bar has already set.
        if (document.querySelector('pdp-sticky-atc.is-visible')) return;
        document.documentElement.style.removeProperty('--sticky-atc-height');
        document.body.classList.remove('has-sticky-atc');
      }

      // Show once the main Add to cart has scrolled off the top of the screen.
      observe() {
        const target =
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector(`#MainProduct-${this.sectionId} .product__info-container`);
        if (!target || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(([entry]) => {
          this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        this.observer.observe(target);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.visible && this.reserveSpace());
          this.resizeObserver.observe(this);
        }
      }

      setVisible(visible) {
        if (visible === this.visible) return;
        this.visible = visible;
        this.classList.toggle('is-visible', visible);
        // Hidden bar stays out of the tab order and away from screen readers.
        this.toggleAttribute('inert', !visible);
        document.body.classList.toggle('has-sticky-atc', visible);
        if (visible) this.reserveSpace();
      }

      // Bottom padding on the page so the bar never covers the footer, and scroll padding
      // so an element focused mid-page is never hidden behind it (WCAG 2.4.11).
      reserveSpace() {
        document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
      }

      follow(variantId) {
        if (!this.select) return;
        const option = Array.from(this.select.options).find((item) => item.value === String(variantId));
        if (!option) {
          this.setButton(false, window.variantStrings?.unavailable || 'Unavailable');
          return;
        }
        if (this.select.value !== option.value) this.select.value = option.value;
        this.apply(option);
      }

      apply(option) {
        if (!option) return;
        const data = option.dataset;
        const available = data.available === 'true';

        this.idInput.value = option.value;
        this.idInput.disabled = !available;

        const price = this.querySelector('[data-sticky-price]');
        const buttonPrice = this.querySelector('[data-sticky-button-price]');
        const compare = this.querySelector('[data-sticky-compare]');
        const compareValue = this.querySelector('[data-sticky-compare-value]');
        if (price) price.textContent = data.price;
        if (buttonPrice) buttonPrice.textContent = data.price;
        if (compare && compareValue) {
          compareValue.textContent = data.compare || '';
          compare.hidden = !data.compare;
        }

        const image = this.querySelector('[data-sticky-image]');
        if (image && data.image) {
          image.src = data.image;
          image.removeAttribute('srcset');
        }

        if (this.qtyInput) {
          const min = parseInt(data.min, 10) || 1;
          this.qtyInput.min = min;
          this.qtyInput.dataset.min = min;
          this.qtyInput.step = parseInt(data.step, 10) || 1;
          if (data.max) {
            this.qtyInput.max = data.max;
            this.qtyInput.dataset.max = data.max;
          } else {
            this.qtyInput.removeAttribute('max');
            delete this.qtyInput.dataset.max;
          }
          if (parseInt(this.qtyInput.value, 10) < min) this.qtyInput.value = min;
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.setButton(available, available ? window.variantStrings?.addToCart : window.variantStrings?.soldOut);
      }

      // Also clears the sold-out lock <product-form> sets after a failed add.
      setButton(enabled, text) {
        if (!this.button) return;
        if (this.productForm) this.productForm.error = false;
        this.productForm?.handleErrorMessage?.();
        this.button.removeAttribute('aria-disabled');
        this.soldOutText?.classList.add('hidden');
        this.label?.classList.remove('hidden');
        this.button.toggleAttribute('disabled', !enabled);
        if (this.label && text) this.label.textContent = text;
      }

      // Select the same variant in the page's own picker, so price, stock and images follow.
      selectOnPage(option) {
        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!picker || !option?.dataset.options) return;

        let values;
        try {
          values = JSON.parse(option.dataset.options);
        } catch (e) {
          return;
        }

        let changed = null;
        values.forEach((value, index) => {
          const position = index + 1;
          const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
            (input) => input.name.endsWith(`-${position}`) && input.value === value
          );
          if (radio) {
            if (!radio.checked) {
              radio.checked = true;
              changed = radio;
            }
            return;
          }
          const select = picker.querySelectorAll('select')[index];
          if (select && select.value !== value) {
            select.value = value;
            changed = select;
          }
        });

        if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}
